import { Icon } from "@iconify/react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { toPng } from "html-to-image";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTable } from "react-table";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import "../style/components-style/SystemBoundary.css";

const impactCategories = [
  { id: "gwp", label: "GWP (kg CO₂e)" },
  { id: "odp", label: "ODP (kg CFC-11e)" },
  { id: "ep_marine", label: "EP-marine (kg N)" },
  { id: "ap", label: "AP (kg SO₂e)" },
  { id: "pocp", label: "POCP (kg C₂H₄e)" },
];
const SystemBoundary = forwardRef(({ processData, title = "System Boundary", isEpd = 0, isBuilding = false }, parentRef) => {
  const ref = useRef();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const toggleCategories = () => setShowAllCategories(!showAllCategories);

  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));
  const defaultData = useMemo(
    () => ({
      A1: "-",
      A2: "-",
      A3: "-",
      A4: "-",
      A5: "-",
      B1: "-",
      B2: "-",
      B3: "-",
      B4: "-",
      B5: "-",
      ...(isBuilding && { B6: "-", B7: "-" }), //  B6, B7 only for buildings
      C1: "-",
      C2: "-",
      C3: "-",
      C4: "-",
      D: "-",
      Total: "-",
    }),
    [isBuilding]
  );

  const data = useMemo(() => {
    return impactCategories
      .map((category) => {
        const rowData = { ...defaultData, category: category.label };
        const cumulativeImpact = {};
        // console.log(processData, "DCE");
        processData.forEach((item) => {
          const prefix = item.lca_stage.split(": ")[0];
          const impactValue = item[category.id] || 0;
          // console.log(prefix, impactValue, category, item);
          cumulativeImpact[prefix] = (cumulativeImpact[prefix] || 0) + impactValue;
          // console.log(cumulativeImpact[prefix]);
        });
        // console.log(cumulativeImpact);

        if (isEpd === 1) {
          // Calculate totals for each stage group
          const productStageTotal = ["A1", "A2", "A3", "A1-A3"].reduce((sum, stage) => sum + (cumulativeImpact[stage] || 0), 0);
          // console.log(productStageTotal);
          const constructionStageTotal = ["A4", "A5", "A4-A5"].reduce((sum, stage) => sum + (cumulativeImpact[stage] || 0), 0);
          const useStageTotal = ["B1", "B2", "B3", "B4", "B5", "B1-B5"].reduce((sum, stage) => sum + (cumulativeImpact[stage] || 0), 0);
          const endOfLifeStageTotal = ["C1", "C2", "C3", "C4", "C1-C4"].reduce((sum, stage) => sum + (cumulativeImpact[stage] || 0), 0);

          // Set the merged values for each stage group
          rowData.A1 = productStageTotal;
          rowData.A2 = 0;
          rowData.A3 = 0;
          rowData.A4 = constructionStageTotal;
          rowData.A5 = 0;
          rowData.B1 = useStageTotal;
          rowData.B2 = 0;
          rowData.B3 = 0;
          rowData.B4 = 0;
          rowData.B5 = 0;
          if (isBuilding) {
            rowData.B6 = cumulativeImpact["B6"] || cumulativeImpact["B6-B7"] || 0;
            rowData.B7 = cumulativeImpact["B7"] || 0;
          }
          rowData.C1 = endOfLifeStageTotal;
          rowData.C2 = 0;
          rowData.C3 = 0;
          rowData.C4 = 0;
          rowData.D = cumulativeImpact.D || 0;
        } else {
          // Keep all values separate
          Object.keys(cumulativeImpact).forEach((stage) => {
            if (rowData.hasOwnProperty(stage)) {
              rowData[stage] = `${cumulativeImpact[stage]}`;
            }
          });
        }

        const total = Object.values(cumulativeImpact).reduce((sum, value) => sum + (value || 0), 0);
        rowData.Total = total;

        // console.log(JSON.stringify(cumulativeImpact));
        return rowData;
      })
      .filter((i) => i.Total !== 0 || i.category === impactCategories[0].label);
  }, [defaultData, processData, isEpd, isBuilding]);

  const columns = useMemo(() => {
    const cellValueFormatter = {
      Cell: ({ value }) => {
        // Handle missing or invalid data
        if (value === "-" || value === null || value === undefined) return "-";
        if (typeof value === "string" && isNaN(Number(value))) return "-";
        if (isNaN(value)) return "-";

        // Return whole numbers without decimals
        return value === 0 ? 0 : Math.round(value).toLocaleString("en-CA");
      },
    };
    return [
      {
        Header: t("Impact Category"),
        accessor: "category",
        width: 150,
        columns: [
          {
            Header: "",
            accessor: "category",
            subHeader: t("Impact Category"),
            backgroundColor: isDark ? "#27272a" : "#FAFCFF",
          },
        ],
      },
      {
        Header: t("Product stage"),
        accessor: "product_stage",
        columns: [
          {
            Header: isEpd === 1 ? "A1-A3" : "A1",
            accessor: "A1",
            subHeader: isEpd === 1 ? t("Product Stage Total") : t("Raw Material Acquisition"),
            backgroundColor: isDark ? "#1e3a5f" : "#dbeafe",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "A2",
            accessor: "A2",
            subHeader: t("Transport to Factory"),
            backgroundColor: isDark ? "#1e3a5f" : "#dbeafe",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "A3",
            accessor: "A3",
            subHeader: t("Manufacturing"),
            backgroundColor: isDark ? "#1e3a5f" : "#dbeafe",
            ...cellValueFormatter,
          },
        ].filter(Boolean),
      },
      {
        Header: t("Construction stage"),
        accessor: "construction_stage",
        columns: [
          {
            Header: isEpd === 1 ? "A4-A5" : "A4",
            accessor: "A4",
            subHeader: isEpd === 1 ? t("Construction Stage Total") : t("Transport to Site"),
            backgroundColor: isDark ? "#14532d" : "#dcfce7",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "A5",
            accessor: "A5",
            subHeader: t("Construction-Installation"),
            backgroundColor: isDark ? "#14532d" : "#dcfce7",
            ...cellValueFormatter,
          },
        ].filter(Boolean),
      },
      {
        Header: t("Use stage"),
        accessor: "use_stage",
        columns: [
          {
            Header: isEpd === 1 ? "B1-B5" : "B1",
            accessor: "B1",
            subHeader: isEpd === 1 ? t("Use Stage Total") : t("Use"),
            backgroundColor: isDark ? "#1c1c1e" : "#f8fafc",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "B2",
            accessor: "B2",
            subHeader: t("Maintenance"),
            backgroundColor: isDark ? "#1c1c1e" : "#f8fafc",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "B3",
            accessor: "B3",
            subHeader: t("Repair"),
            backgroundColor: isDark ? "#1c1c1e" : "#f8fafc",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "B4",
            accessor: "B4",
            subHeader: t("Replacement"),
            backgroundColor: isDark ? "#1c1c1e" : "#f8fafc",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "B5",
            accessor: "B5",
            subHeader: t("Refurbishment"),
            backgroundColor: isDark ? "#1c1c1e" : "#f8fafc",
            ...cellValueFormatter,
          },
        ].filter(Boolean),
      },
      // Add Operational stage for buildings only (B6, B7)
      ...(isBuilding
        ? [
          {
            Header: t("Operational stage"),
            accessor: "operational_stage",
            columns: [
              {
                Header: "B6",
                accessor: "B6",
                subHeader: t("Operational Energy Use"),
                backgroundColor: isDark ? "#78350f" : "#fef3c7",
                ...cellValueFormatter,
              },
              {
                Header: "B7",
                accessor: "B7",
                subHeader: t("Operational Water Use"),
                backgroundColor: isDark ? "#78350f" : "#fef3c7",
                ...cellValueFormatter,
              },
            ],
          },
        ]
        : []),
      {
        Header: t("End-of-life stage"),
        accessor: "end_of_life_stage",
        columns: [
          {
            Header: isEpd === 1 ? "C1-C4" : "C1",
            accessor: "C1",
            subHeader: isEpd === 1 ? t("End-of-Life Stage Total") : t("Deconstruction / Demolition"),
            backgroundColor: isDark ? "#3b0764" : "#ede9fe",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "C2",
            accessor: "C2",
            subHeader: t("Transport to Waste / Disposal"),
            backgroundColor: isDark ? "#3b0764" : "#ede9fe",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "C3",
            accessor: "C3",
            subHeader: t("Waste Processing"),
            backgroundColor: isDark ? "#3b0764" : "#ede9fe",
            ...cellValueFormatter,
          },
          !isEpd && {
            Header: "C4",
            accessor: "C4",
            subHeader: t("Disposal of Waste"),
            backgroundColor: isDark ? "#3b0764" : "#ede9fe",
            ...cellValueFormatter,
          },
        ].filter(Boolean),
      },
      {
        Header: t("Beyond the building life cycle"),
        accessor: "beyond_building_life_cycle",
        columns: [
          {
            Header: "D",
            accessor: "D",
            subHeader: t("Reuse-Recovery-Recycling Potential"),
            backgroundColor: isDark ? "#431407" : "#fef3c7",
            width: 50,
            ...cellValueFormatter,
          },
        ],
      },
      {
        Header: t("Total"),
        accessor: "Total",
        backgroundColor: isDark ? "#1e293b" : "#f1f5f9",
        width: 50,
        ...cellValueFormatter,
      },
    ];
  }, [isEpd, t, isBuilding]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  const filteredRows = useMemo(() => {
    if (showAllCategories) return rows;
    // console.log(rows);
    // console.log(rows.filter((row, index) => index === 0));
    return rows.filter((row, index) => index === 0);
  }, [rows, showAllCategories]);

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Paper variant="Table Container" sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t(title)}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box className="system-boundary-container" sx={{ p: 4, overflowX: "auto", display: "block" }}>
        <Box ref={ref} sx={{ minWidth: "max-content", width: "100%" }}>
          <table {...getTableProps()} className={`boundary-table ${isBuilding ? "building-mode" : ""}`} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              {headerGroups.map((headerGroup, groupIndex) => {
                const { key, ...restGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={`header-group-${groupIndex}`} {...restGroupProps}>
                    {headerGroup.headers.map((column, columnIndex) => {
                      const { key, ...restGroupProps } = column.getHeaderProps();
                      return (
                        <th
                          key={`header-${groupIndex}-${columnIndex}`}
                          {...restGroupProps}
                          colSpan={column.columns?.length || 1}
                          style={{
                            background: isDark ? tok.bg.tableHeader : "#FAFCFF",
                            textAlign: "center",
                            border: `1px solid ${tok.border.divider}`,
                            color: isDark ? tok.text.gridHeader : "inherit",
                          }}>
                          <div className="header-content">
                            {column.id === "category" && data.length > 1 ? (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Icon icon={showAllCategories ? "mdi:minus" : "mdi:plus"} style={{ marginRight: 8, cursor: "pointer" }} onClick={toggleCategories} />
                                {column.render("Header")}
                              </div>
                            ) : (
                              column.render("Header")
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
              {headerGroups.map((headerGroup, groupIndex) => {
                const { key, ...restGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={`subheader-group-${groupIndex}`} {...restGroupProps}>
                    {headerGroup.headers.map((column, columnIndex) =>
                      column.columns?.map((subColumn, subColumnIndex) => (
                        <th
                          key={`subheader-${groupIndex}-${columnIndex}-${subColumnIndex}`}
                          style={{
                            background: subColumn.backgroundColor,
                            textAlign: "center",
                            border: `1px solid ${tok.border.divider}`,
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            color: isDark ? "#ffffff" : "inherit",
                          }}>
                          <div className="header-content">
                            <span className="subheader">{subColumn.subHeader}</span>
                          </div>
                        </th>
                      ))
                    )}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {filteredRows.map((row, rowIndex) => {
                prepareRow(row);
                const { key, ...restGroupProps } = row.getRowProps();
                return (
                  <tr key={`row-${rowIndex}`} {...restGroupProps}>
                    {row.cells.map((cell, cellIndex) => {
                      const subColumn = columns.flatMap((col) => col.columns || []).find((c) => c && c.accessor === cell.column.id);
                      const { key, ...otherProps } = cell.getCellProps();
                      return (
                        <td
                          key={`cell-${rowIndex}-${cellIndex}`}
                          {...otherProps}
                          style={{
                            textAlign: "center",
                            border: `1px solid ${tok.border.divider}`,
                            background: subColumn ? subColumn.backgroundColor : undefined,
                            color: isDark ? "#ffffff" : "inherit",
                          }}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>
    </Paper>
    </Box>
  );
});

export default SystemBoundary;
